import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';
import { Field, useForm, useFormState } from 'react-final-form';

import WithLabel, { LABEL_STYLES } from '../../components/with-label/WithLabel';
import WithError from '../../components/with-error/WithError';
import PasswordInput from '../../components/common-inputs/PasswordInput';
import CommonButton from '../../components/common-button/CommonButton';
import passwordGenerator from '../../utils/passwordGeneration';
import Icon from '../../icons/Icon';
import { ICONS } from '../../icons';

import scss from './ChangePasswordModal.module.scss';

const ChangeUserPasswordForm = ({ handleSubmit }) => {
  const { t } = useTranslation('userManagement');
  const isLoading = useSelector((state) => state.userManagement.isLoading);
  const { valid } = useFormState();
  const isSubmitDisabled = !valid || isLoading;
  const form = useForm();

  const handleGeneratePassword = useCallback(() => {
    const newPassword = passwordGenerator();
    form.change('password', newPassword);
    form.change('repeatPassword', newPassword);
  }, [form]);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <h2 className={scss.title}>{t('changePassword')}</h2>

      <div className={scss.fields}>
        <Field name='oldPassword' autoComplete='new-password'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.COLUMN} label={t('oldPassword')}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
                <PasswordInput
                  size='sm'
                  placeholder={t('password')}
                  isError={meta.touched && meta.error}
                  formProps={input}
                  disabled={isLoading}
                  autoComplete='new-password'
                  LeftIcon={<Icon icon={ICONS.Lock} disableCommonStyles />}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='password' autoComplete='new-password'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.COLUMN} label={t('newPassword')}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
                <PasswordInput
                  size='sm'
                  placeholder={t('password')}
                  isError={meta.touched && meta.error}
                  formProps={input}
                  disabled={isLoading}
                  autoComplete='new-password'
                  LeftIcon={<Icon icon={ICONS.Lock} disableCommonStyles />}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='repeatPassword' autoComplete='new-password'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.COLUMN} label={t('reenterNewPassword')}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
                <PasswordInput
                  size='sm'
                  placeholder={t('password')}
                  isError={meta.touched && meta.error}
                  formProps={input}
                  disabled={isLoading}
                  autoComplete='new-password'
                  LeftIcon={<Icon icon={ICONS.Lock} disableCommonStyles />}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>
      </div>

      <div className={scss.actions}>
        <CommonButton
          type='button'
          styleColor='secondary'
          onClick={handleGeneratePassword}
          maxWidth={170}
          disabled={isLoading}>
          {t('generatePassword')}
        </CommonButton>

        <CommonButton
          type='submit'
          styleColor='primary'
          onClick={handleSubmit}
          maxWidth={170}
          disabled={isSubmitDisabled}>
          {isLoading ? <ThreeDots height={15} width={40} /> : null}
          {t('changePassword')}
        </CommonButton>
      </div>
    </form>
  );
};

export default ChangeUserPasswordForm;
